import { Layout } from "@/components/layout/Layout";
import { TenantImage } from "@/components/home/TenantImage";
import { LoadingLabel, Skel, SkelLines } from "@/components/home/TenantSkeletons";
import { PageExtraSections } from "@/components/sections/PageExtraSections";
import { usePageExtraSections } from "@/hooks/usePageExtraSections";
import { isAwaitingFirstResult, useTenantOrgId } from "@/hooks/useTenantOrgId";
import { Check } from "lucide-react";
import { renderDisplayText } from "@/lib/utils";
import { getIconComponent } from "@/components/admin/icon-map";
import { useOrg } from "@/contexts/OrgContext";
import {
  mergeTeamMembersIntoAbout,
  useAboutContent,
  useOrgTeamMembers,
  type AboutValue,
  type AboutTeamMember,
} from "@/hooks/useAboutContent";
import { useServiceAreas, useCompanyInfoBrief } from "@/hooks/useCompanyInfo";
import aboutHeroImage from "@/assets/about-hero.jpg";
import cleanLivingRoom from "@/assets/clean-living-room.jpg";
import resultsLivingRoom from "@/assets/results-living-room.jpg";
import resultsKitchen from "@/assets/results-kitchen.jpg";
import resultsBathroom from "@/assets/results-bathroom.jpg";
import { PageSeo } from "@/components/seo/PageSeo";
import { Results } from "@/components/home/Results";
import { CTAPanel } from "@/components/home/CTAPanel";
import { useTenantPath } from "@/hooks/useTenantPath";
import { useDemoShowcase } from "@/hooks/useDemoShowcase";

const defaultValues: AboutValue[] = [
  {
    icon: "Heart",
    title: "Passion",
    description: "We genuinely love what we do. Every clean is an opportunity to make someone\u2019s day better.",
  },
  {
    icon: "Shield",
    title: "Trust",
    description: "Your home is your sanctuary. We treat it with the respect and care it deserves.",
  },
  {
    icon: "Users",
    title: "Community",
    description: "We employ local cleaners and are proud to serve our community.",
  },
  {
    icon: "Award",
    title: "Excellence",
    description: "We never cut corners. Every surface, every room \u2013 nothing less than perfect.",
  },
];

const defaultFeatures = ["Online scheduling", "Transparent pricing", "Fast response times"];

const defaultResults = {
  title: "See Our Results",
  subtitle: "",
  images: [
    { url: resultsLivingRoom, alt: "Spotless living room after cleaning" },
    { url: resultsKitchen, alt: "Sparkling clean kitchen" },
    { url: resultsBathroom, alt: "Fresh and clean bathroom" },
  ],
};

interface TeamCardMember {
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  initials: string;
}

const initialsOf = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

/** Tenant-authored team members store an image URL in `avatar` + explicit `initials`. */
function normalizeContentTeam(members: AboutTeamMember[]): TeamCardMember[] {
  return members.map((m) => ({
    name: m.name ?? "",
    role: m.role ?? "",
    bio: m.bio ?? "",
    imageUrl: m.avatar || undefined,
    initials: m.initials || initialsOf(m.name ?? ""),
  }));
}

/** Legacy founder/placeholder teams store initials (never an image) in `avatar`. */
function normalizeDefaultTeam(
  members: { name: string; role: string; bio: string; avatar: string }[],
): TeamCardMember[] {
  return members.map((m) => ({ name: m.name, role: m.role, bio: m.bio, initials: m.avatar }));
}

const POLISHED_VERSION_ORG_ID = 'a90138ee-26a5-4570-97b0-5a1144b9ad85';

const polishedMilestones = [
  { year: "2018", event: "The founders first met while working together in operations management within the financial services sector, discovering a shared approach to problem solving and high standards" },
  { year: "2023", event: "Founders launch first successful business venture" },
  { year: "2024", event: "We launch our property management company, based in South Wales" },
  { year: "2025", event: "We expand into cleaning services, partnering with established and trusted local partners" },
  { year: "2025", event: "We leverage cutting edge technology to offer web hosting and business management tools to small businesses in need" },
];

const polishedTeam = [
  {
    name: "Nick Patel",
    role: "Founder and Managing Director",
    bio: "With over 10 years in multi industry operational management, Nick brings a practical, people focused approach to leadership and a strong commitment to high standards in everything he oversees.",
    avatar: "NP",
  },
  {
    name: "Chris Thomas",
    role: "Founder and AI Architect",
    bio: "With a background in financial services, entrepreneurship and award winning leisure services, Chris brings a blend of strategic thinking and creativity to every project he works on.",
    avatar: "CT",
  },
  {
    name: "Kirstie Harries",
    role: "Customer Experience Lead",
    bio: "Kirstie is the first point of contact for our clients, using modern tools and technology to streamline communication, maximise efficiency and deliver a smooth, professional experience from start to finish.",
    avatar: "KH",
  },
];

const placeholderTeam = [
  { name: "Your founder", role: "Owner & Operator", bio: "Add a short bio in the About admin to introduce yourself.", avatar: "YF" },
  { name: "Team member", role: "Cleaning Specialist", bio: "Add team members in the About admin so customers can put a face to the service.", avatar: "TM" },
  { name: "Team member", role: "Cleaning Specialist", bio: "Add team members in the About admin so customers can put a face to the service.", avatar: "TM" },
];

const placeholderMilestones = [
  { year: new Date().getFullYear().toString(), event: "We launched! Edit this About page in your admin to tell your real story." },
];

const AboutPage = () => {
  const { org } = useOrg();
  const orgId = useTenantOrgId();
  const tenantPath = useTenantPath();
  // On /demo the page sells the product: showcase prompts replace the founder
  // story/team (which stay on the org's own subdomain). Saved About content still wins.
  const demo = useDemoShowcase();
  const demoAbout = demo?.about ?? null;
  const isPolished = !demo && orgId === POLISHED_VERSION_ORG_ID;
  const aboutPendingQuery = useAboutContent(orgId);
  const { data: aboutContent } = aboutPendingQuery;
  const aboutPending = isAwaitingFirstResult(aboutPendingQuery);
  const teamPendingQuery = useOrgTeamMembers(orgId);
  const { data: teamSource = [] } = teamPendingQuery;
  const teamPending = isAwaitingFirstResult(teamPendingQuery);
  const areasPendingQuery = useServiceAreas(orgId);
  const { data: serviceAreas = [] } = areasPendingQuery;
  const areasPending = isAwaitingFirstResult(areasPendingQuery);
  const companyPendingQuery = useCompanyInfoBrief(orgId);
  const { data: companyInfo } = companyPendingQuery;
  const companyPending = isAwaitingFirstResult(companyPendingQuery);
  const { data: extraSections } = usePageExtraSections(orgId, "about");
  // Everything on this page is tenant copy with placeholder fallbacks; hold a
  // skeleton until it is known so the placeholders never flash first.
  const contentPending = aboutPending || teamPending || areasPending || companyPending;
  const syncedAboutContent = mergeTeamMembersIntoAbout(aboutContent ?? null, teamSource);

  const areasText = serviceAreas.length === 0
    ? 'your area'
    : serviceAreas.length === 1
      ? serviceAreas[0]
      : serviceAreas.slice(0, 2).join(' & ');

  const milestones = syncedAboutContent?.milestones?.length
    ? syncedAboutContent.milestones
    : demoAbout ? demoAbout.milestones : isPolished ? polishedMilestones : placeholderMilestones;
  const businessName = demo?.businessName || companyInfo?.company_name || org.name;
  const journeyTitle = syncedAboutContent?.journeyTitle || demoAbout?.journeyTitle || "Our Journey";
  const journeyImageUrl = syncedAboutContent?.journeyImageUrl || cleanLivingRoom;
  const defaultIntroCopy = demoAbout
    ? demoAbout.intro
    : isPolished
    ? "We started The Polished Version with a simple mission to provide reliable, high quality cleaning services people can genuinely trust. No rushed jobs or cutting corners, just honest and thorough work. That mission has grown into something bigger as we now support other businesses too, helping them improve their online presence and manage their operations with more confidence and ease."
    : `We started ${businessName} with a simple mission — to provide reliable, high quality cleaning that people can genuinely trust. No rushed jobs, no cutting corners. Edit this paragraph in your admin to tell your story.`;

  // Drive the public page from the tenant's edited About content when present,
  // falling back to the founder/placeholder copy so the page is never blank.
  const heroTitle = syncedAboutContent?.hero?.title || demoAbout?.heroTitle || `Making ${areasText} homes sparkle`;
  const introCopy = syncedAboutContent?.hero?.subtitle || defaultIntroCopy;
  const heroImageUrl = syncedAboutContent?.hero?.imageUrl || aboutHeroImage;
  const features = syncedAboutContent?.hero?.features?.length ? syncedAboutContent.hero.features : defaultFeatures;
  const valuesTitle = syncedAboutContent?.valuesTitle || demoAbout?.valuesTitle || "What We Stand For";
  const values = syncedAboutContent?.values?.length ? syncedAboutContent.values : defaultValues;
  const teamTitle = syncedAboutContent?.teamTitle || demoAbout?.teamTitle || "Meet Our Team";
  const team = syncedAboutContent?.team?.length
    ? normalizeContentTeam(syncedAboutContent.team)
    : demoAbout
    ? normalizeContentTeam(demoAbout.team)
    : normalizeDefaultTeam(isPolished ? polishedTeam : placeholderTeam);
  const results = syncedAboutContent?.results ?? defaultResults;
  const resultImages = results.images?.length ? results.images : defaultResults.images;
  const ctaSubtitle = syncedAboutContent?.cta?.subtitle
    || demoAbout?.ctaSubtitle
    || (isPolished
      ? "Join over 500 happy customers across Cardiff & Newport"
      : `Find out why customers across ${areasText} choose ${businessName}`);

  const ctaTitle = syncedAboutContent?.cta?.title || demoAbout?.ctaTitle || "Join the many households and businesses choosing a cleaner, easier way to manage their space";
  const ctaButtonText = syncedAboutContent?.cta?.buttonText || "Book my cleaning";

  if (contentPending) {
    return (
      <Layout>
        <section className="relative -mt-20 overflow-hidden pb-16 pt-[120px] md:pb-24 md:pt-36" aria-busy="true">
          <LoadingLabel>Loading</LoadingLabel>
          <div className="tp-hero-wash" aria-hidden />
          <div className="tp-skeleton container relative mx-auto px-5 md:px-6" aria-hidden>
            <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-6">
                <Skel className="h-3 w-40 tp-skel-round" />
                <div className="tp-display tp-display--md mt-6"><SkelLines lines={2} widths={["88%", "56%"]} /></div>
                <div className="tp-lead mt-7"><SkelLines lines={4} widths={["100%", "97%", "94%", "48%"]} /></div>
                <div className="mt-9 flex flex-wrap gap-2.5">
                  {[96, 120, 104].map((width) => <Skel key={width} className="h-9 tp-skel-round" style={{ width }} />)}
                </div>
              </div>
              <div className="lg:col-span-6">
                <Skel className="tp-skel-media aspect-[4/3] w-full lg:aspect-[5/4.6]" />
              </div>
            </div>
          </div>
        </section>
        <div className="min-h-[60vh]" aria-hidden />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageSeo pageSlug="about" fallbackTitle={`About ${businessName}`} fallbackDescription={`Learn more about ${businessName}.`} />
      {/* Hero */}
      <section className="relative -mt-20 overflow-hidden pb-16 pt-[120px] md:pb-24 md:pt-36">
        <div className="tp-hero-wash" aria-hidden />
        <div className="container relative mx-auto px-5 md:px-6">
          <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-6">
              <p className="tp-eyebrow" data-reveal>{demoAbout ? demoAbout.eyebrow : `About ${businessName}`}</p>
              <h1 className="tp-display tp-display--md mt-6" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>
                {renderDisplayText(heroTitle)}
              </h1>
              <p className="tp-lead mt-7" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
                {introCopy}
              </p>
              <ul className="mt-9 flex flex-wrap gap-2.5" data-reveal style={{ ["--reveal-delay" as string]: "200ms" }}>
                {features.map((feature) => (
                  <li key={feature} className="tp-chip">
                    <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6" data-reveal="mask" style={{ ["--reveal-delay" as string]: "120ms" }}>
              <div className="tp-media tp-shadow-lg relative aspect-[4/3] lg:aspect-[5/4.6]">
                <TenantImage
                  src={heroImageUrl}
                  alt={`${businessName} team`}
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Timeline */}
      <section className="tp-section tp-tint">
        <div className="container mx-auto px-5 md:px-6">
          <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-6">
              <p className="tp-eyebrow" data-reveal>Our story</p>
              <h2 className="tp-h2 mt-5" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>{renderDisplayText(journeyTitle)}</h2>
              <ol className="relative mt-12">
                <span className="absolute bottom-3 left-[5px] top-3 w-px bg-foreground/10" aria-hidden />
                {milestones.map((milestone, index) => (
                  <li
                    key={`milestone-${index}`}
                    className="relative flex gap-6 pb-9 pl-8 last:pb-0"
                    data-reveal
                    style={{ ["--reveal-delay" as string]: `${index * 70}ms` }}
                  >
                    <span className="absolute left-0 top-1.5 h-[11px] w-[11px] rounded-full border-2 border-primary bg-background" aria-hidden />
                    <span className="tp-numeral w-14 shrink-0 pt-0.5 text-base text-primary">{milestone.year}</span>
                    <p className="text-foreground/85 leading-relaxed">{milestone.event}</p>
                  </li>
                ))}
              </ol>
            </div>
            <div className="lg:sticky lg:top-28 lg:col-span-6" data-reveal="mask">
              <div className="tp-media tp-shadow-md relative aspect-[4/3] lg:aspect-[4/5]">
                <TenantImage
                  src={journeyImageUrl}
                  alt={`${businessName} journey`}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="tp-section">
        <div className="container mx-auto px-5 md:px-6">
          <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <p className="tp-eyebrow" data-reveal>Our values</p>
              <h2 className="tp-h2 mt-5" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>
                {renderDisplayText(valuesTitle)}
              </h2>
            </div>
            <p className="tp-lead lg:col-span-5 lg:pb-1" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
              {renderDisplayText(demoAbout?.valuesLead ?? "These values guide everything we do")}
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 md:mt-16 lg:grid-cols-4">
            {values.map((value, index) => {
              const ValueIcon = getIconComponent(value.icon || "Shield");
              return (
                <div
                  key={`${value.title}-${index}`}
                  className="tp-card flex flex-col p-7"
                  data-reveal
                  style={{ ["--reveal-delay" as string]: `${index * 80}ms` }}
                >
                  <span className="tp-icon-chip">
                    <ValueIcon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <h3 className="tp-h3 mt-8">
                    {renderDisplayText(value.title || "")}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="tp-section tp-tint">
        <div className="container mx-auto px-5 md:px-6">
          <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <p className="tp-eyebrow" data-reveal>The team</p>
              <h2 className="tp-h2 mt-5" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>
                {renderDisplayText(teamTitle)}
              </h2>
            </div>
            <p className="tp-lead lg:col-span-5 lg:pb-1" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
              {renderDisplayText(demoAbout?.teamLead ?? `The people behind ${businessName}`)}
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:mt-16 md:grid-cols-2 lg:grid-cols-3">
            {team.map((member, index) => (
              <div
                key={`${member.name}-${index}`}
                className="tp-card flex flex-col p-7 md:p-8"
                data-reveal
                style={{ ["--reveal-delay" as string]: `${(index % 3) * 90}ms` }}
              >
                <div className="flex items-center gap-4">
                  {member.imageUrl ? (
                    <TenantImage
                      src={member.imageUrl}
                      alt={member.name}
                      className="h-16 w-16 shrink-0 rounded-full object-cover ring-4 ring-secondary"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground ring-4 ring-secondary">
                      {member.initials}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="tp-h4 text-lg">
                      {renderDisplayText(member.name)}
                    </h3>
                    <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary">{member.role}</p>
                  </div>
                </div>
                <p className="mt-6 border-t tp-hairline pt-6 text-sm leading-relaxed text-muted-foreground">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Gallery */}
      <Results
        eyebrow="Results"
        title={(syncedAboutContent?.results?.title) || demoAbout?.resultsTitle || defaultResults.title}
        description={results.subtitle || demoAbout?.resultsDescription || `What a clean from ${businessName} looks like`}
        images={resultImages}
      />

      {/* CTA */}
      <PageExtraSections sections={extraSections} />

      <CTAPanel
        title={ctaTitle}
        subtitle={ctaSubtitle}
        buttonText={ctaButtonText}
        buttonHref={tenantPath("/booking")}
        image={heroImageUrl}
      />
      <div className="h-10 md:h-16" aria-hidden />
    </Layout>
  );
};

export default AboutPage;
